import { CaretRight } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  /** Display label */
  label: string;
  /** Link destination (renders an anchor) */
  href?: string;
  /** Click handler (renders a button) */
  onClick?: () => void;
}

interface BreadcrumbProps {
  /** Ordered path items -- last item is treated as current page */
  items: BreadcrumbItem[];
  className?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Breadcrumb({ items, className }: BreadcrumbProps) {
  return (
    <nav aria-label="Navegação estrutural" className={className}>
      <ol className="flex min-w-0 flex-nowrap items-center gap-1 text-sm"> {/* FEAT-052 TASK-013 */}
        {items.map((item, idx) => {
          const isLast = idx === items.length - 1;

          return (
            <li
              key={idx}
              className={cn(
                'inline-flex min-w-0 items-center gap-1', // FEAT-052 TASK-013
                isLast ? 'shrink-[0.5]' : 'shrink',
                !isLast && idx > 0 && 'max-sm:hidden', // intermediate crumbs fold below sm
                // non-interactive "..." (CSS only, not part of the trail text) where they were
                isLast && items.length > 2 && "max-sm:before:content-['…'] max-sm:before:text-text-tertiary",
              )}
            >
              {/* Separator */}
              {idx > 0 && (
                <CaretRight
                  size={12}
                  className="text-text-tertiary shrink-0"
                  aria-hidden="true"
                />
              )}

              {/* Current page (last item) */}
              {isLast ? (
                <span
                  className="text-text-primary font-medium truncate max-w-[40vw] sm:max-w-[200px]"
                  aria-current="page"
                >
                  {item.label}
                </span>
              ) : item.href ? (
                /* Link item */
                <a
                  href={item.href}
                  className={cn(
                    'text-text-secondary hover:text-text-primary transition-colors truncate max-w-[40vw] sm:max-w-[200px]',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded',
                  )}
                >
                  {item.label}
                </a>
              ) : item.onClick ? (
                /* Button item */
                <button
                  type="button"
                  onClick={item.onClick}
                  className={cn(
                    'text-text-secondary hover:text-text-primary transition-colors truncate max-w-[40vw] sm:max-w-[200px] cursor-pointer',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus rounded',
                  )}
                >
                  {item.label}
                </button>
              ) : (
                /* Plain text (no interaction) */
                <span className="text-text-secondary truncate max-w-[40vw] sm:max-w-[200px]">
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
